import   axios                from 'axios';

import { API_URLS           } from '../urls';
import { BackendResponse    ,
         handleError        } from '../../dataTypes/backendServicesManagment/types';

import { Cart                ,
         CartItem            } from '../../dataTypes/purchaseManagment/types';

const transformCartItem = (data: any): CartItem => ({
  productCode:        data.productCode,
  productVariantCode: data.productVariantCode,
  quantity:           data.quantity,
  priceCode:          data.priceCode,
  basePriceCopy:      data.basePriceCopy,
  taxRateCopy:        data.taxRateCopy,
  priceToPayCopy:     data.priceToPayCopy,
  totalPrice:         data.totalPrice,
});

const transformCart = (data: any): Cart => ({
  id:           data.id,
  amount:       data.amount,
  comment:      data.comment,
  ipAddress:    data.ipAddress,
  creationDate: data.creationDate ? new Date(data.creationDate) : null,
  soldItems:    Array.isArray(data.soldItems) ? data.soldItems.map(transformCartItem) : [],
});

// ── Panier (achat non payé) du créateur connecté ─────────────────────────────
//
// purchase_infos_by_email?type=unpaid crée automatiquement un panier vide côté
// serveur s'il n'en existe pas encore (voir PurchaseInfosByEmail.php) : il n'y a
// donc jamais besoin d'appeler PurchaseCreation séparément avant d'ajouter un
// premier article.

export interface BackendResponse_Cart extends BackendResponse<Cart> {}

export const getCart = async (email: string): Promise<BackendResponse_Cart> => {

  const url = `${API_URLS.getPurchaseInfosByEmail}?email=${encodeURIComponent(email)}&type=unpaid`;

  try {
    const response = await axios.get(url);

    if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
      return {
        code: 1,
        comment: 'Le panier a été récupéré avec succès.',
        payload: transformCart(response.data[0]),
      };
    } else {
      return handleError<Cart>(response.status, 'Réponse non-200 ou panier introuvable.');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<Cart>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<Cart>(-102, 'Erreur inconnue.');
  }
};

const patchHeaders = {
  'Accept':       'application/ld+json',
  'Content-Type': 'application/merge-patch+json',
};

export interface BackendResponse_Void extends BackendResponse<null> {}

export const addItemToCart = async ( purchaseId:          number ,
                                     productVariantCode:  string ,
                                     priceCode:           string ,
                                     quantity:            number ,
                                     description:         string = '' ): Promise<BackendResponse_Void> => {

  const url = API_URLS.patchAddItemToCart.replace('{id}', `${purchaseId}`);

  try {
    const response = await axios.patch(
      url,
      { description, quantityStr: `${quantity}`, productVariantCode, priceCode },
      { headers: patchHeaders }
    );

    if (response.status === 200) {
      return { code: 1, comment: "Article ajouté au panier.", payload: null };
    }
    return handleError<null>(response.status, 'Réponse non-200.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<null>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<null>(-102, 'Erreur inconnue.');
  }
};

export const removeItemFromCart = async ( purchaseId:         number ,
                                          productVariantCode: string ): Promise<BackendResponse_Void> => {

  const url = API_URLS.patchRemoveItemFromCart.replace('{id}', `${purchaseId}`);

  try {
    const response = await axios.patch(
      url,
      { productVariantCode },
      { headers: patchHeaders }
    );

    if (response.status === 200) {
      return { code: 1, comment: "Article retiré du panier.", payload: null };
    }
    return handleError<null>(response.status, 'Réponse non-200.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<null>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<null>(-102, 'Erreur inconnue.');
  }
};

export const changeQuantityOfItem = async ( purchaseId:         number ,
                                            productVariantCode: string ,
                                            priceCode:          string ,
                                            quantity:           number ): Promise<BackendResponse_Void> => {

  const url = API_URLS.patchChangeQuantityOfItem.replace('{id}', `${purchaseId}`);

  try {
    const response = await axios.patch(
      url,
      { quantityStr: `${quantity}`, productVariantCode, priceCode },
      { headers: patchHeaders }
    );

    if (response.status === 200) {
      return { code: 1, comment: "Quantité mise à jour.", payload: null };
    }
    return handleError<null>(response.status, 'Réponse non-200.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<null>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<null>(-102, 'Erreur inconnue.');
  }
};

// ── Paiement ──────────────────────────────────────────────────────────────────

export interface BackendResponse_PaymentUrl extends BackendResponse<string> {}

export const createPayment = async ( amount:     number ,
                                     purchaseId: number ,
                                     bankCode:   string ,
                                     userEmail:  string ,
                                     comment:    string ): Promise<BackendResponse_PaymentUrl> => {

  const url = API_URLS.postPaymentCreation;

  try {
    const response = await axios.post(
      url,
      {
        amountStr:  `${amount}`,
        purchaseId: `${purchaseId}`,
        bankCode,
        userEmail,
        comment,
      },
      { headers: { 'Accept': 'application/ld+json', 'Content-Type': 'application/ld+json' } }
    );

    if (response.status === 201 && response.data?.url) {
      return { code: 1, comment: "Paiement créé.", payload: response.data.url };
    }
    return handleError<string>(response.status, 'Réponse non-201 ou url manquante.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<string>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<string>(-102, 'Erreur inconnue.');
  }
};
